import { StatusData } from "@fairfleet/geotab";

/** One point in time, with a value per selected diagnostic that reported at it. */
export type ChartPoint = Record<string, number> & { timestamp: number };

export interface ChartDataset {
  points: ChartPoint[];
  /** The span the points cover, or `null` when there are none. */
  fromMs: number | null;
  toMs: number | null;
}

const EMPTY: ChartDataset = { points: [], fromMs: null, toMs: null };

/**
 * Pivots the readings into what a line chart wants: one row per instant, one column per series.
 *
 * Readings arrive as a flat list of (diagnostic, time, value). Two diagnostics rarely report at
 * the same instant, so most rows carry a value for one series and nothing for the others — which
 * is what makes the chart draw a line through the gaps rather than back to zero.
 *
 * @param statusData - Every reading in the period, in any order.
 * @param diagnosticIds - The diagnostics to chart.
 * @returns The points in time order, and the span they cover.
 */
export function buildChartDataset(
  statusData: StatusData[],
  diagnosticIds: string[],
): ChartDataset {
  if (diagnosticIds.length === 0) {
    return EMPTY;
  }

  const wanted = new Set(diagnosticIds);
  const byTimestamp = new Map<number, ChartPoint>();
  let fromMs: number | null = null;
  let toMs: number | null = null;

  for (const entry of statusData) {
    const diagnosticId = entry.diagnostic?.id;
    if (!diagnosticId || !wanted.has(diagnosticId)) {
      continue;
    }

    const timestamp = new Date(entry.dateTime).getTime();
    const value = Number(entry.data);
    if (!Number.isFinite(timestamp) || !Number.isFinite(value)) {
      continue;
    }

    if (fromMs === null || timestamp < fromMs) fromMs = timestamp;
    if (toMs === null || timestamp > toMs) toMs = timestamp;

    const point = byTimestamp.get(timestamp) ?? ({ timestamp } as ChartPoint);
    point[diagnosticId] = value;
    byTimestamp.set(timestamp, point);
  }

  const points = [...byTimestamp.values()].sort((a, b) => a.timestamp - b.timestamp);

  return { points, fromMs, toMs };
}
